// ChecklistTemplateItemsField.jsx

import React from 'react';

// CSS
import './EnumOptionsField.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Button from 'components/button/Button';
import Checkbox from 'components/checkbox/Checkbox';
import ModalField from 'components/modal/field/ModalField';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import TextInput from 'components/textinput/TextInput';

// Utilities
import { getChecklistTemplateItemLabel } from 'utilities/ChecklistTemplateItems';

/**
 * ChecklistTemplateItemsField
 *
 * Label-only template item editor for checklist fields (Field modal / Studio).
 *
 * @returns {ReactNode}
 */
const ChecklistTemplateItemsField = ({
  checklistItems = [],
  itemLabelDrafts = {},
  setItemLabelDrafts,
  onItemAdd,
  onItemRemove,
  onItemLabelCommit,
  onItemMoveByDelta,
  allowCustomItems = false,
  onAllowCustomItemsChange,
  theme,
}) => {
  const items = Array.isArray(checklistItems) ? checklistItems : [];

  return (
    <>
      <ModalField>
        <ModalLabel label="Template items" />
        <ModalHint hint="Shared items seeded on every record. Keys are generated automatically." />

        {items.length === 0 ? (
          <div
            className="field-modal-empty"
            style={{ color: theme?.foregroundMuted }}
          >
            No template items yet. Leave empty for a freeform list, or add items for a shared checklist.
          </div>
        ) : (
          <div className="field-modal-options">
            {items.map((item, itemIndex) => {
              const itemId = item?.id || item?.key || '';
              const itemLabel = getChecklistTemplateItemLabel(item);
              const hasLabelDraft = itemLabelDrafts
                && Object.prototype.hasOwnProperty.call(itemLabelDrafts, itemId);
              const draftLabel = hasLabelDraft ? itemLabelDrafts[itemId] : itemLabel;
              const canMoveUp = itemIndex > 0;
              const canMoveDown = itemIndex < items.length - 1;

              return (
                <div className="field-modal-option" key={itemId || itemIndex}>
                  <TextInput
                    value={draftLabel}
                    onChange={(event) => {
                      const nextLabel = event.target.value;
                      setItemLabelDrafts?.((prev) => ({
                        ...(prev || {}),
                        [itemId]: nextLabel,
                      }));
                    }}
                    onBlur={() => onItemLabelCommit?.(itemId, draftLabel)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        onItemLabelCommit?.(itemId, draftLabel);
                        event.currentTarget.blur();
                      }
                    }}
                    placeholder="Item label"
                  />
                  <button
                    type="button"
                    className="field-modal-option-handle"
                    title="Move up"
                    aria-label="Move item up"
                    disabled={!canMoveUp}
                    onClick={() => onItemMoveByDelta?.(itemId, -1)}
                  >
                    <Icon
                      iconKey="keyboard_arrow_up"
                      width={18}
                      height={18}
                      color={theme?.foregroundMuted}
                    />
                  </button>
                  <button
                    type="button"
                    className="field-modal-option-handle"
                    title="Move down"
                    aria-label="Move item down"
                    disabled={!canMoveDown}
                    onClick={() => onItemMoveByDelta?.(itemId, 1)}
                  >
                    <Icon
                      iconKey="keyboard_arrow_down"
                      width={18}
                      height={18}
                      color={theme?.foregroundMuted}
                    />
                  </button>
                  <div
                    className="field-modal-option-remove"
                    title="Remove item"
                    aria-label="Remove item"
                    onClick={() => onItemRemove?.(itemId)}
                  >
                    <Icon
                      iconKey="do_not_disturb_on"
                      width={20}
                      height={20}
                      color={theme?.foregroundMuted}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="field-modal-options-toolbar">
          <Button
            label="Add Item"
            height="38px"
            borderRadius="9px"
            enabled
            onClick={onItemAdd}
          />
        </div>
      </ModalField>

      <ModalField>
        <ModalLabel label="Checklist behavior" />
        <Checkbox
          value={allowCustomItems === true}
          onChange={() => onAllowCustomItemsChange?.(!(allowCustomItems === true))}
          size="16"
          label="Allow custom items"
        />
        <ModalHint hint="Extra per-record items do not count toward completion." />
      </ModalField>
    </>
  );
};

ChecklistTemplateItemsField.displayName = 'ChecklistTemplateItemsField';

export default ChecklistTemplateItemsField;
